import { useEffect, useRef, useState, type DependencyList } from 'react';

export interface ScrollFades {
  /** There is content scrolled out of view above. */
  top: boolean;
  /** There is content scrolled out of view below. */
  bottom: boolean;
}

/**
 * Which edges of a scroll container have more content beyond them, so they
 * can fade out. With the scrollbar hidden, a hard-clipped last item otherwise
 * reads as broken (merged into whatever sits below) rather than "scroll for
 * more". Re-measures on scroll, on resize, and when `deps` change (e.g. the
 * rail switching between labels and glyphs).
 */
export function useScrollFades<T extends HTMLElement>(deps: DependencyList = []) {
  const ref = useRef<T>(null);
  const [fades, setFades] = useState<ScrollFades>({ top: false, bottom: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const top = el.scrollTop > 1;
      const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setFades((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
    };

    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(el);

    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  return { ref, fades };
}
